import React, { useState, useRef } from 'react';
import { Heading, Button, Copy } from 'components/components/atoms';
import { Container } from 'components/shared';
import { animated } from '@react-spring/web';
import cx from 'classnames';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import styled from 'styled-components';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import breakpoints from 'utils/breakpoints';
import { scrollToSection } from 'utils/scrollTo';
import { isValidTitle } from 'utils/validateTitle';
import useNewColorSchemaOptions from 'utils/hooks/useNewColorSchemaOptions';

const Grid = styled(animated.div)`
  ${({ columns }) =>
    !isNaN(columns) &&
    `grid-template-columns: repeat(${columns}, minmax(0, 1fr));`}
`;

const Article = styled.article`
  &:last-child {
    padding-bottom: 0;
  }
`;

const H3 = styled.h3`
  @media ${breakpoints.xl} {
    padding-right: 3.2rem;
  }
`;

const MAX_ITEMS = 16;

const getImageSrc = (item, isMobile) => {
  if (item && item.image) {
    if (isMobile) return item.image.srcMobile || item.image.src;
    return item.image.srcDesktop || item.image.src;
  }
  return '';
};

const IconBlock = ({
  items,
  title,
  titleStyle = 'small',
  text = '',
  textStyle,
  numberColumns,
  buttonColor,
  sectionId = '',
  isAmenities = false,
}) => {
  const {
    showNewSchemaColor,
    newSchemaTextColorHexaCode,
    newSchemaColorHexaCode,
  } = useNewColorSchemaOptions();
  const textColor = textStyle ? textStyle.split('-')[0] : null;
  const breakpoint = useBreakpoint();
  const isMobile = ['sm', 'xs'].includes(breakpoint);
  const [buttonTouch, isButtonTouch] = useState(false);
  const [activeFocus, SetActiveFocus] = useState(-1);
  const gridRef = useRef();
  const ItemRefEndOfRow = useRef();
  const ItemRefStartOfRow = useRef();

  let columns = isAmenities ? 2 : 1;
  let itemsPerMobile = isAmenities ? 8 : 9;
  if (breakpoint === 'md') {
    columns = 3;
  } else if (['lg', 'xl', '2xl'].includes(breakpoint)) {
    columns = numberColumns ? parseInt(numberColumns) : 4;
  }

  let START_ROWS_DESKTOP = 1;
  let START_ROWS_MOBILE = 1;
  if (columns === 3) {
    const calculatedRows = Math.ceil(items.length / 3);
    START_ROWS_DESKTOP = calculatedRows <= 3 ? calculatedRows : 3;
    START_ROWS_MOBILE =
      items.length >= itemsPerMobile ? itemsPerMobile : items.length;
  } else {
    const calculatedRows = Math.ceil(items.length / 4);
    START_ROWS_DESKTOP = calculatedRows <= 2 ? calculatedRows : 2;
    START_ROWS_MOBILE =
      items.length >= itemsPerMobile
        ? itemsPerMobile / columns
        : items.length / columns;
  }
  // Derived on every render, never seeded into state: useBreakpoint() reports
  // SSR_BREAKPOINT ('xs') on the server and on the client's first pass, and the
  // real value only after mount. Filling this in from a mount effect left the
  // server-rendered grid empty (slice(0, columns * 0)), and a useState
  // initializer would instead freeze the desktop grid at the mobile row count.
  // Only the SHOW MORE clicks belong in state.
  const baseRows = isMobile ? START_ROWS_MOBILE : START_ROWS_DESKTOP;
  const [extraRows, setExtraRows] = useState(0);
  const visibleRows = baseRows + extraRows;

  const filteredItems = items.slice(0, MAX_ITEMS);
  const visibleItems = items.slice(0, columns * visibleRows);
  const visibleItemsCount = columns * visibleRows;

  const moreRowsToShow = visibleRows * columns < filteredItems.length;

  const resetVisibleRows = () => {
    setExtraRows(0);
    scrollToSection(sectionId);
  };

  return (
    <Container className='items-center'>
      {isValidTitle(title) && (
        <Heading
          as='h2'
          color={showNewSchemaColor ? 'black' : textColor || 'blue'}
          className='text-4xl leading-10 text-center'
        >
          {title}
        </Heading>
      )}
      {text && (
        <div className='mb-16'>
          <Copy
            color={showNewSchemaColor ? 'black' : textColor || 'blue'}
            className='text-xl text-center px-10 md:px-64'
          >
            {text}
          </Copy>
        </div>
      )}
      <Grid
        key={breakpoint}
        className={cx(
          'overflow-wrap: break-words grid gap-x-16  gap-y-0 md:mt-16 pt-2 select-none overflow-hidden',
          {
            'md:grid-cols-3 lg:grid-cols-4': !columns,
          },
        )}
        columns={columns}
        rows={visibleRows}
        ref={gridRef}
      >
        {visibleItems.map((i, index) => {
          const lastItem = columns * visibleRows;
          const key = index < lastItem ? i.title + index : i.title + index + 0;
          const itemSrc = getImageSrc(i, isMobile);
          return (
            <Article
              data-aos='fade-up'
              className={cx('pb-10', {
                invisible: index >= lastItem,
              })}
              key={key}
            >
              {itemSrc && (
                <div className='mb-5'>
                  <img
                    className='object-contain h-16 w-16'
                    src={itemSrc}
                    alt={'' /* i.image.alt*/}
                  />
                </div>
              )}
              <H3
                ref={index === 0 ? ItemRefStartOfRow : null}
                className={cx('font-bold font-trade-gothic-20 mb-1 pr-2', {
                  'text-3xl': titleStyle === 'small',
                  'text-4xl': titleStyle === 'large',
                  'text-mattamy-blue':
                    !showNewSchemaColor && textColor !== 'white',
                  'text-white': textColor && textColor === 'white',
                  'text-black': showNewSchemaColor,
                })}
              >
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: i.title }}
                ></RichText>
              </H3>
              <p
                className={cx('font-medium leading-relaxed', {
                  'text-mattamy-blue':
                    !showNewSchemaColor && textColor && textColor === 'blue',
                  'text-white':
                    !showNewSchemaColor && textColor && textColor !== 'blue',
                  'text-action-blue': !showNewSchemaColor && !textColor,
                  'text-black': showNewSchemaColor,
                })}
              >
                <RichText
                  ref={index + 1 === visibleItemsCount ? ItemRefEndOfRow : null}
                  tag='span'
                  className='richtext'
                  field={{ value: i.text }}
                ></RichText>
              </p>
            </Article>
          );
        })}
      </Grid>
      {((!isMobile && filteredItems.length / columns > START_ROWS_DESKTOP) ||
        (isMobile && filteredItems.length / columns > START_ROWS_MOBILE)) && (
        <div className='justify-center flex'>
          <Button
            className={cx('mt-12 md:mt-4', {
              'focus:bg-mattamy-blue-dark focus:text-white':
                !showNewSchemaColor && buttonTouch,
              'hover:bg-mattamy-blue-dark hover:text-white':
                !showNewSchemaColor && !isMobile,
            })}
            showNewSchemaColor={showNewSchemaColor}
            newSchemaColorHexaCode={newSchemaColorHexaCode}
            color={showNewSchemaColor ? 'black' : buttonColor || 'action-blue'}
            variant={showNewSchemaColor ? 'outlined' : 'filled'}
            buttonDescription={moreRowsToShow ? 'SHOW MORE' : 'SHOW LESS'}
            onClick={() => {
              if (isMobile) {
                isButtonTouch(true);
                setTimeout(() => {
                  isButtonTouch(false);
                }, 1000);
              }
              if (moreRowsToShow) {
                if (isMobile) {
                  const rowsLeft = filteredItems.length - visibleRows;
                  let rows = rowsLeft;
                  if (!columns) {
                    rows = rowsLeft > 4 ? 4 : rowsLeft;
                  }
                  setExtraRows((n) => n + rows);
                } else {
                  setExtraRows((n) => n + 1);
                  if (ItemRefEndOfRow && ItemRefEndOfRow.current)
                    ItemRefEndOfRow.current.focus();
                }
              } else {
                if (ItemRefEndOfRow && ItemRefEndOfRow.current)
                  ItemRefStartOfRow.current.focus();
                resetVisibleRows();
              }
            }}
          >
            <p className='w-40'>{moreRowsToShow ? 'SHOW MORE' : 'SHOW LESS'}</p>
          </Button>
        </div>
      )}
    </Container>
  );
};

export default IconBlock;
